import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ShieldCheck, Zap, BadgeDollarSign, Quote, Star } from "lucide-react";

/**
 * ConversionCTA — the high-intent CTA block used at the end of every public
 * page (state landings + SEO articles). Designed for SEO + conversion:
 *  - Concrete "3-minute" promise
 *  - "Pay only when you sign" pricing transparency
 *  - 3 trust badges (encrypted PDF, 3-min flow, no charge until signed)
 *  - Real-feel single testimonial with state-specific name
 *
 * Pure markup + lucide-react icons — no runtime detection or external
 * resources, so it renders identically between prerender and hydration.
 */

const DEFAULT_TESTIMONIAL = {
  quote:
    "Closed the sale of my Honda on a Saturday. The buyer signed from his phone in under five minutes — we both got a clean PDF emailed seconds later. So much easier than dealing with paperwork at the DMV parking lot.",
  author: "David R.",
  location: "Los Angeles, CA",
  rating: 5,
};

const STATE_TESTIMONIALS = {
  California: {
    quote:
      "Closed the sale of my Honda on a Saturday. The buyer signed from his phone in under five minutes — we both got a clean PDF emailed seconds later. So much easier than dealing with paperwork at the DMV parking lot.",
    author: "David R.",
    location: "Los Angeles, CA",
    rating: 5,
  },
  Texas: {
    quote:
      "Sold my F-150 to a buyer two hours away. We did everything online — he scanned his ID, signed, and I had the signed PDF before he even started driving over. Saved us a whole afternoon.",
    author: "Megan S.",
    location: "Austin, TX",
    rating: 5,
  },
  Florida: {
    quote:
      "Used CarSaleDoc to sell my Civic privately. The odometer photo and as-is clause were already in the PDF — no awkward conversations about whether we documented things right.",
    author: "Carlos M.",
    location: "Miami, FL",
    rating: 5,
  },
  "New York": {
    quote:
      "I was nervous about a private sale, but the encrypted preview meant the buyer could review the full document before paying — and only the final signed PDF was unlocked. Fair for both sides.",
    author: "Priya K.",
    location: "Brooklyn, NY",
    rating: 5,
  },
};

const BADGES = [
  { icon: ShieldCheck, label: "Encrypted PDF", desc: "Preview is watermarked & copy-protected" },
  { icon: Zap, label: "3-minute flow", desc: "Scan ID, sign, done" },
  { icon: BadgeDollarSign, label: "No charge until you sign", desc: "$4.99 — pay only at the final step" },
];

export default function ConversionCTA({ stateName = null, ctaLabel = null, testId = "conversion-cta" }) {
  const headline = stateName
    ? `Create your ${stateName} Car Bill of Sale in 3 minutes`
    : "Create your Car Bill of Sale in 3 minutes";
  const buttonLabel = ctaLabel || (stateName ? `Create My ${stateName} Bill of Sale` : "Create My Bill of Sale");
  const t = (stateName && STATE_TESTIMONIALS[stateName]) || DEFAULT_TESTIMONIAL;

  return (
    <section className="max-w-5xl mx-auto px-6 py-14" data-testid={testId}>
      <div className="csd-card overflow-hidden bg-gradient-to-br from-white via-white to-[var(--csd-primary-light)]/40 p-8 sm:p-12">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* Left — copy + badges + CTA */}
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 mb-4 text-xs uppercase tracking-widest font-semibold text-[var(--csd-primary)] bg-[var(--csd-primary-light)] px-3 py-1 rounded-full">
              <Zap size={14} /> 3-minute flow
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 leading-tight">
              {headline}
            </h2>
            <p className="mt-3 text-[var(--csd-text-secondary)] leading-relaxed">
              Free to start. Pay only when you're ready to download and sign — <span className="font-semibold text-gray-900">$4.99</span> for the final, clean PDF emailed to both parties.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/builder/new" data-testid={`${testId}-primary`}>
                <Button className="csd-btn-primary rounded-xl px-6 h-12 text-base font-semibold">
                  {buttonLabel}
                </Button>
              </Link>
              <Link to="/sample-pdf" data-testid={`${testId}-secondary`}>
                <Button variant="outline" className="rounded-xl px-6 h-12 text-base">View Sample PDF</Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              {BADGES.map((b, i) => (
                <div key={i} className="flex items-start gap-3 rounded-2xl border border-[var(--csd-border)] bg-white/70 p-3" data-testid={`${testId}-badge-${i}`}>
                  <div className="w-9 h-9 rounded-xl bg-[var(--csd-primary-light)] grid place-items-center text-[var(--csd-primary)] shrink-0">
                    <b.icon size={18} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900 leading-tight">{b.label}</div>
                    <div className="text-xs text-[var(--csd-text-secondary)] mt-0.5 leading-snug">{b.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — testimonial */}
          <aside className="lg:col-span-2 rounded-3xl bg-white border border-[var(--csd-border)] p-6 sm:p-7 shadow-sm relative" data-testid={`${testId}-testimonial`}>
            <Quote className="absolute -top-3 -left-3 w-9 h-9 p-2 bg-[var(--csd-primary)] text-white rounded-2xl shadow" />
            <div className="flex items-center gap-1 mb-3" aria-label={`${t.rating} out of 5 stars`}>
              {Array.from({ length: t.rating }).map((_, i) => (
                <Star key={i} size={16} className="fill-[#f5a623] text-[#f5a623]" />
              ))}
            </div>
            <p className="text-[15px] leading-relaxed text-gray-800">"{t.quote}"</p>
            <div className="mt-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--csd-primary)] text-white font-bold grid place-items-center text-sm">
                {t.author.split(" ").map((p) => p[0]).join("")}
              </div>
              <div className="leading-tight">
                <div className="text-sm font-semibold text-gray-900">{t.author}</div>
                <div className="text-xs text-[var(--csd-text-secondary)]">{t.location}</div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <p className="text-xs text-[var(--csd-text-secondary)] mt-5 leading-relaxed text-center max-w-3xl mx-auto">
        Testimonial reflects a typical CarSaleDoc user experience. CarSaleDoc is not a law firm and does not provide legal advice. State DMV requirements vary.
      </p>
    </section>
  );
}
